import React, { useState } from 'react';
import axios from 'axios';
import { getHeaders } from '../../../lib/requests';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import GeneralButton from '../button_general';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { handlePdfFile, extractErrorMessage } from '../../../lib/utils';
import MenuMultiSelect from '../multiselect/multiselect_menu';
import { useNavigate } from 'react-router-dom';

function ProductionReportModal({ isOpen, onClose }) {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState('scheduled');
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handlePrint = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');

      const response = await axios.post(
        `${apiUrl}/reports/production_items_types/pdf`,
        {
          start_date: startDate?.toISOString().split('T')[0],
          end_date: endDate?.toISOString().split('T')[0],
          delivery_status: deliveryStatus || 'scheduled',
          menu_ids: selectedMenus.length > 0 ? selectedMenus.map(m => m.menu_id) : null
        },
        {
          headers: getHeaders(apiToken),
          responseType: 'blob',
        }
      );

      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'production_sheet.pdf';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }
      
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      handlePdfFile(pdfBlob, fileName, 'open');
    } catch (error) {
      console.error('Error generating PDF:', error);
      if (
        (error.response && [401, 403].includes(error.response.status)) ||
        !localStorage.getItem('accessToken')
      ) {
        onClose?.();
        navigate('/login?sessionExpired=true', { replace: true });
        return;
      }
      const message = await extractErrorMessage(error);
      alert(`Failed to generate PDF: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white w-full max-w-lg rounded shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Create Production Report</h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Start Date</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              dateFormat="yyyy-MM-dd"
              className="border rounded w-full p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">End Date</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              dateFormat="yyyy-MM-dd"
              className="border rounded w-full p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Delivery Status</label>
            <select
              value={deliveryStatus}
              onChange={(e) => setDeliveryStatus(e.target.value)}
              className="border rounded w-full p-2"
            >
              <option value="scheduled">Scheduled</option>
              <option value="delivered">Delivered</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
          <div></div>
          <div className="col-span-2">
            <MenuMultiSelect
              label="Menu"
              onSelect={setSelectedMenus}
              selectedMenus={selectedMenus}
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-2">
          <GeneralButton type="button" buttonType="secondary" onClick={onClose}>
            Cancel
          </GeneralButton>
          <GeneralButton
            type="button"
            buttonType="primary"
            onClick={handlePrint}
            disabled={!startDate || !endDate || loading}
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
                Generating...
              </span>
            ) : (
              "Create Report"
            )}
          </GeneralButton>
        </div>
      </div>
    </div>
  );
}

export default ProductionReportModal;
